import React, { useEffect, useState } from "react";
import Swal from "sweetalert2";
import PropTypes from "prop-types";
import { Spin } from "antd";
import { getApiBaseUrl, ADD_ACTIVITY, LOGIN } from "../Config/Config";
const EditPrinter = ({ printerObj }) => {
  const user = JSON.parse(sessionStorage.getItem("user") || "{}");

  const [formData, setFormData] = useState({
    name: "",
    ipadress: "",
  });
  const [loading, setLoading] = useState(false);
  const [askPassword, setAskPassword] = useState(false);
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [authLoading, setAuthLoading] = useState(false);
  const [error, setError] = useState("");
  const [changes, setChanges] = useState({});

  useEffect(() => {
    if (printerObj) {
      setFormData({
        name: printerObj?.name || "",
        ipadress: printerObj?.ipadress || "",
      });
    }
  }, [printerObj]);

  const fieldNameMap = {
    name: "Name",
    ipadress: "IP Address",
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => {
      const newForm = { ...prev, [name]: value };
      const originalValue = printerObj?.[name] || "";

      const mappedName = fieldNameMap[name] || name; // convert to FilterType/Description

      if (originalValue !== value) {
        setChanges((prevChanges) => ({
          ...prevChanges,
          [`Old${mappedName}`]: originalValue,
          [`New${mappedName}`]: value,
        }));
      } else {
        setChanges((prevChanges) => {
          const updated = { ...prevChanges };
          delete updated[`Old${mappedName}`];
          delete updated[`New${mappedName}`];
          return updated;
        });
      }
      return newForm;
    });
  };

  const handleAuth = async () => {
    if (!password) {
      setError("Please enter your password.");
      return;
    }

    setAuthLoading(true);
    setError("");

    try {
      if (!user?.UserName) {
        throw new Error("User name not found in session data.");
      }

      const payload = {
        username: user.UserName,
        password,
      };

      const res = await fetch(`${getApiBaseUrl()}${LOGIN}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      if (data.status) {
        setAskPassword(false);
        setPassword("");
        await handleSubmit();
      } else {
        throw new Error(
          data.Message || "Invalid credentials. Please try again."
        );
      }
    } catch (err) {
      setError(err.message || "Unable to authenticate. Please try again.");
    } finally {
      setAuthLoading(false);
    }
  };

  const handleSubmit = async () => {
    setLoading(true);

    const user = JSON.parse(sessionStorage.getItem("user") || "{}");
    const userId = user?.UserID || 0;

    const payload = {
      id: printerObj.id,
      name: formData.name,
      ipadress: formData.ipadress,
      UpdatedBy: userId,
    };

    const logActivity = async (isSuccess, notes) => {
      try {
        await fetch(`${getApiBaseUrl()}${ADD_ACTIVITY}`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            ActivityType: 1,
            PerformedBy: userId,
            PerformedOn: new Date().toISOString(),
            Notes: notes,
            Location: "updatePrinter",
            IsSucces: isSuccess,
          }),
        });
      } catch (err) {
        console.error("Failed to log activity:", err);
      }
    };

    try {
      const response = await fetch(
        `${getApiBaseUrl()}/api/EditprinterIpAdress`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );

      const result = await response.json();
      const status = result?.ResultData?.[0]?.Status;
      const message = " Printer Updated successfully.";
      const finalNote = Object.entries(changes)
        .map(([key, value]) => `${key}: ${value}`)
        .join(", ");
      if (response.ok && status === "Success") {
        await logActivity(1, "Updated Printer: " + finalNote);
        Swal.fire({
          icon: "success",
          title: "Success",
          text: message,
          confirmButtonText: "OK",
        }).then(() => {
          window.location.reload();
        });
      } else {
        Swal.fire({ icon: "error", title: "Failed", text: message });
        await logActivity(0, "Failed to update Printer: " + finalNote);
      }
    } catch (error) {
      console.error("Network error:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to connect to server.",
      });
      await logActivity(0, "Network error during printer update");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdatePrinter = async (e) => {
    e.preventDefault();

    if (!user?.UserID) {
      setError("Invalid user data. Please log in again.");
      Swal.fire({
        icon: "error",
        title: "User Data Error",
        text: "Invalid user data. Please log in again.",
      });
      return;
    }

    setAskPassword(true);
  };

  const clearFormData = () => {
    setFormData({
      name: "",
      ipadress: "",
    });
    setAskPassword(false);
    setPassword("");
    setError("");
  };

  useEffect(() => {
    const offcanvas = document.getElementById("EditPrinterOffcanvas");
    if (offcanvas) {
      const handleClose = () => {
        clearFormData();
      };
      offcanvas.addEventListener("hidden.bs.offcanvas", handleClose);
      return () => {
        offcanvas.removeEventListener("hidden.bs.offcanvas", handleClose);
      };
    }
  }, [user]);

  return (
    <>
      {(loading || authLoading) && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
          style={{
            zIndex: 1050,
            backdropFilter: "blur(4px)",
            backgroundColor: "rgba(255, 255, 255, 0.2)",
          }}
        >
          <Spin size="large" tip="Processing..." />
        </div>
      )}

      <div
        className="offcanvas offcanvas-end"
        tabIndex="-1"
        id="EditPrinterOffcanvas"
      >
        <form onSubmit={handleUpdatePrinter}>
          <div className="offcanvas-header">
            <h5 className="offcanvas-title">Edit Printer</h5>
            <div className="d-flex align-items-center ms-auto">
              <button
                type="submit"
                className="btn btn-sm btn-outline-success me-2"
                title="Save"
              >
                <i className="fa fa-check fs-4"></i>
              </button>
              <button
                type="button"
                className="btn-close text-reset"
                data-bs-dismiss="offcanvas"
                aria-label="Close"
              ></button>
            </div>
          </div>
          <div className="offcanvas-body">
            <div className="mb-3">
              <label className="form-label">Printer Name</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className="form-control"
                required
              />
            </div>

            <div className="mb-3">
              <label className="form-label">IP Address</label>
              <input
                type="text"
                name="ipadress"
                value={formData.ipadress}
                onChange={handleChange}
                className="form-control"
                required
              />
            </div>
            {askPassword && (
              <div className="mt-2">
                <div className="mb-3 w-lg-75 m-auto">
                  <label className="form-label">
                    Confirm Password <span className="text-danger"> *</span>
                  </label>
                  <div className="pass-group">
                    <input
                      type={showPassword ? "text" : "password"}
                      name="password"
                      className="pass-input form-control"
                      value={password}
                      onChange={(e) => {
                        setPassword(e.target.value);
                        setError("");
                      }}
                      onCopy={(e) => e.preventDefault()}
                      onPaste={(e) => e.preventDefault()}
                      onCut={(e) => e.preventDefault()}
                      placeholder="Enter Password"
                    />
                    <span
                      className={`ti toggle-password ${
                        showPassword ? "ti-eye" : "ti-eye-off"
                      }`}
                      onClick={() => setShowPassword(!showPassword)}
                    ></span>
                  </div>
                </div>
                <button
                  type="button"
                  className="btn btn-primary btn-sm me-2"
                  onClick={handleAuth}
                  disabled={authLoading || !password}
                >
                  {authLoading ? "Verifying..." : "Confirm"}
                </button>
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={() => {
                    setAskPassword(false);
                    setPassword("");
                    setError("");
                  }}
                >
                  Cancel
                </button>
              </div>
            )}
            {error && (
              <div className="alert alert-danger" role="alert">
                {error}
              </div>
            )}
          </div>
        </form>
      </div>
    </>
  );
};

EditPrinter.propTypes = {
  printerObj: PropTypes.object,
};

export default EditPrinter;
